import { useEffect, useState } from "react";
import { Plus, Search, UserRound, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FIT } from "@/constants/testIds";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", goal: "", membership: "" });

  const load = async () => {
    const { data } = await api.get("/fitness/clients");
    setClients(data);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/fitness/clients", form);
      toast.success(`${form.name} added.`);
      setOpenNew(false);
      setForm({ name: "", email: "", goal: "", membership: "" });
      await load();
    } catch (err) {
      toast.error("Could not save client.");
    }
  };

  const filtered = clients.filter(
    (c) =>
      !query ||
      c.name?.toLowerCase().includes(query.toLowerCase()) ||
      c.goal?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="px-4 md:px-8 py-8 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Fitness</div>
          <h1 className="font-display text-4xl">Client roster</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {clients.length} clients · sorted by newest
          </p>
        </div>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Search clients"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9 w-64"
            />
          </div>
          <Button
            data-testid={FIT.newClient}
            onClick={() => setOpenNew(true)}
            className="rounded-full gap-2"
          >
            <Plus className="h-4 w-4" /> New client
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((c) => (
          <Link
            to={`/app/fitness/clients/${c.id}`}
            key={c.id}
            data-testid={FIT.clientCard(c.id)}
            className="group rounded-xl border border-border bg-card p-5 hover:-translate-y-0.5 transition-transform"
          >
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-full bg-muted overflow-hidden grid place-items-center shrink-0">
                {c.photo_url ? (
                  <img src={c.photo_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <UserRound className="h-5 w-5 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-xl leading-tight truncate">{c.name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.email || "—"}</div>
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  {c.goal && (
                    <span className="text-[10px] uppercase tracking-[0.18em] rounded-full border border-border px-2 py-0.5">
                      {c.goal}
                    </span>
                  )}
                  {c.membership && (
                    <span className="text-[10px] uppercase tracking-[0.18em] rounded-full bg-primary/10 text-primary px-2 py-0.5">
                      {c.membership}
                    </span>
                  )}
                </div>
              </div>
              <ArrowUpRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-sm text-muted-foreground text-center py-12">
            No clients yet — add your first to get started.
          </div>
        )}
      </div>

      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-2xl">Add a client</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1.5" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <Label>Goal</Label>
              <Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="e.g. Marathon prep" className="mt-1.5" />
            </div>
            <div>
              <Label>Membership</Label>
              <Input value={form.membership} onChange={(e) => setForm({ ...form, membership: e.target.value })} placeholder="e.g. Studio Unlimited" className="mt-1.5" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="submit" className="rounded-full">Add client</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
